/**
 * Web Vitals tracking for Core Web Vitals metrics
 *
 * Tracks:
 * - LCP (Largest Contentful Paint) - Loading performance
 * - FID (First Input Delay) - Interactivity (deprecated, replaced by INP)
 * - INP (Interaction to Next Paint) - Interactivity responsiveness
 * - CLS (Cumulative Layout Shift) - Visual stability
 * - FCP (First Contentful Paint) - Initial render
 * - TTFB (Time to First Byte) - Server response time
 */

export interface WebVitalMetric {
  name: 'LCP' | 'FID' | 'INP' | 'CLS' | 'FCP' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
}

type ReportCallback = (metric: WebVitalMetric) => void;

// Thresholds based on Google's Core Web Vitals guidelines
const thresholds = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
};

function getRating(name: keyof typeof thresholds, value: number): WebVitalMetric['rating'] {
  const threshold = thresholds[name];
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

function generateUniqueId(): string {
  return `v${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Observe Largest Contentful Paint
 */
function observeLCP(onReport: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  let lcpValue = 0;
  const id = generateUniqueId();

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & { startTime: number };

      if (lastEntry) {
        lcpValue = lastEntry.startTime;
      }
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });

    // Report on page hide (user navigates away)
    const reportLCP = () => {
      if (lcpValue > 0) {
        onReport({
          name: 'LCP',
          value: lcpValue,
          rating: getRating('LCP', lcpValue),
          delta: lcpValue,
          id,
          navigationType: getNavigationType(),
        });
      }
    };

    // Use visibilitychange to catch when user leaves
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        reportLCP();
        observer.disconnect();
      }
    });
  } catch {
    // Silently fail if not supported
  }
}

/**
 * Observe First Input Delay
 */
function observeFID(onReport: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  const id = generateUniqueId();

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const firstEntry = entries[0] as PerformanceEventTiming;

      if (firstEntry) {
        const value = firstEntry.processingStart - firstEntry.startTime;
        onReport({
          name: 'FID',
          value,
          rating: getRating('FID', value),
          delta: value,
          id,
          navigationType: getNavigationType(),
        });
        observer.disconnect();
      }
    });

    observer.observe({ type: 'first-input', buffered: true });
  } catch {
    // Silently fail if not supported
  }
}

/**
 * Observe Interaction to Next Paint (INP)
 */
function observeINP(onReport: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  const id = generateUniqueId();
  let maxINP = 0;

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries() as PerformanceEventTiming[];

      entries.forEach((entry) => {
        // INP is the highest interaction latency
        const duration = entry.duration;
        if (duration > maxINP) {
          maxINP = duration;
        }
      });
    });

    observer.observe({ type: 'event', buffered: true });

    // Report on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && maxINP > 0) {
        onReport({
          name: 'INP',
          value: maxINP,
          rating: getRating('INP', maxINP),
          delta: maxINP,
          id,
          navigationType: getNavigationType(),
        });
      }
    });
  } catch {
    // Silently fail if not supported
  }
}

/**
 * Observe Cumulative Layout Shift
 */
function observeCLS(onReport: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  const id = generateUniqueId();
  let clsValue = 0;
  let sessionValue = 0;
  let sessionEntries: PerformanceEntry[] = [];

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries() as (PerformanceEntry & {
        hadRecentInput: boolean;
        value: number;
      })[];

      entries.forEach((entry) => {
        // Only count if there wasn't user input
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0] as PerformanceEntry | undefined;
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1] as PerformanceEntry | undefined;

          // Start new session if gap > 1s or total > 5s
          if (
            !firstSessionEntry ||
            entry.startTime - (lastSessionEntry?.startTime ?? 0) > 1000 ||
            entry.startTime - firstSessionEntry.startTime > 5000
          ) {
            sessionValue = entry.value;
            sessionEntries = [entry];
          } else {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          }

          // Update CLS if this session is worse
          if (sessionValue > clsValue) {
            clsValue = sessionValue;
          }
        }
      });
    });

    observer.observe({ type: 'layout-shift', buffered: true });

    // Report on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        onReport({
          name: 'CLS',
          value: clsValue,
          rating: getRating('CLS', clsValue),
          delta: clsValue,
          id,
          navigationType: getNavigationType(),
        });
        observer.disconnect();
      }
    });
  } catch {
    // Silently fail if not supported
  }
}

/**
 * Observe First Contentful Paint
 */
function observeFCP(onReport: ReportCallback): void {
  if (!('PerformanceObserver' in window)) return;

  const id = generateUniqueId();

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const fcpEntry = entries.find((e) => e.name === 'first-contentful-paint');

      if (fcpEntry) {
        const value = fcpEntry.startTime;
        onReport({
          name: 'FCP',
          value,
          rating: getRating('FCP', value),
          delta: value,
          id,
          navigationType: getNavigationType(),
        });
        observer.disconnect();
      }
    });

    observer.observe({ type: 'paint', buffered: true });
  } catch {
    // Silently fail if not supported
  }
}

/**
 * Observe Time to First Byte
 */
function observeTTFB(onReport: ReportCallback): void {
  const id = generateUniqueId();

  try {
    const [navigationEntry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

    if (navigationEntry) {
      const value = navigationEntry.responseStart - navigationEntry.requestStart;
      onReport({
        name: 'TTFB',
        value,
        rating: getRating('TTFB', value),
        delta: value,
        id,
        navigationType: getNavigationType(),
      });
    }
  } catch {
    // Silently fail if not supported
  }
}

function getNavigationType(): string {
  const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  return entry?.type ?? 'navigate';
}

/**
 * Initialize Web Vitals tracking
 *
 * @param onReport - Callback function to handle reported metrics
 * @param reportAllMetrics - If true, reports FCP and TTFB in addition to Core Web Vitals
 *
 * @example
 * ```ts
 * import { initWebVitals } from '@/utils/webVitals';
 *
 * initWebVitals((metric) => {
 *   // Send to analytics
 *   gtag('event', metric.name, {
 *     event_category: 'Web Vitals',
 *     value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
 *     event_label: metric.rating,
 *     non_interaction: true,
 *   });
 * });
 * ```
 */
export function initWebVitals(
  onReport: ReportCallback,
  reportAllMetrics = true
): void {
  // Core Web Vitals
  observeLCP(onReport);
  observeFID(onReport);
  observeINP(onReport);
  observeCLS(onReport);

  // Additional metrics
  if (reportAllMetrics) {
    observeFCP(onReport);
    observeTTFB(onReport);
  }
}

/**
 * Default reporter that logs to console in development
 * and can be extended to send to analytics in production
 */
export function createWebVitalsReporter(options?: {
  analyticsEndpoint?: string;
  debug?: boolean;
}): ReportCallback {
  const { analyticsEndpoint, debug = import.meta.env.DEV } = options ?? {};

  return (metric) => {
    // Console logging in development
    if (debug) {
      const ratingColors = {
        good: '#0cce6b',
        'needs-improvement': '#ffa400',
        poor: '#ff4e42',
      };

      console.log(
        `%c[Web Vitals] ${metric.name}: ${metric.value.toFixed(metric.name === 'CLS' ? 3 : 0)}`,
        `color: ${ratingColors[metric.rating]}; font-weight: bold;`,
        `(${metric.rating})`
      );
    }

    // Send to analytics endpoint if configured
    if (analyticsEndpoint) {
      const body = JSON.stringify({
        name: metric.name,
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
        id: metric.id,
        page: window.location.pathname,
        navigationType: metric.navigationType,
      });

      // Use sendBeacon for reliability on page unload
      if (navigator.sendBeacon) {
        navigator.sendBeacon(analyticsEndpoint, body);
      } else {
        fetch(analyticsEndpoint, {
          body,
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    }

    // Google Analytics 4 integration example
    // Uncomment if using gtag
    /*
    if (typeof gtag !== 'undefined') {
      gtag('event', metric.name, {
        event_category: 'Web Vitals',
        value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
        event_label: metric.rating,
        non_interaction: true,
      });
    }
    */
  };
}
